import { formatParticipantDate } from "../../utils/participantDateValidation";
// ParticipantService.ts

import { PagedResults } from "../../interfaces/genericResponse/GenericResponse";
import Participant, { ActiveParticipantDTO, GetAllActiveParticipantsRequest, GetCirListResDto, GetCountOfCIR, GetDetailedFilesForParticipantResponse, GetListOfCirReq, ParticipantUnderMeDto, RegisteredParticipantDTO, UserWaiverDTO } from "../../interfaces/Participant";
import AssignedParticipant from "../../interfaces/participant/AssignedParticipant";
import NewParticipant from "../../interfaces/participant/NewParticipant";
import Waiver from "../../interfaces/participant/Waiver";
import { ApiResponse } from "../../interfaces/genericResponse/GenericResponse";
import { apiGet, apiPatch, apiPost } from "../../utilities/api-client/apiRequest";
import { buildQueryV2 } from "../../utilities/common/buildQueryParams";

export interface MonthFolderDto {
    month: number;
    monthName: string;
    totalFiles: number;
    totalSize: number;
    lastUploadedDate: string;
}

export interface YearFolderDto {
    year: number;
    totalFiles: number;
    totalSize: number;
    lastUploadedDate: string;
    months: MonthFolderDto[];
}

export async function GetParticipantFileFoldersAsync(
    token: string,
    participantId: string
): Promise<ApiResponse<YearFolderDto[]>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/participants/${participantId}/file-folders`;
    try {
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });
        return await response.json();
    } catch (error) {
        console.error('Error fetching folder tree:', error);
        throw error;
    }
}

// Function to fetch all participants of an employee
export async function getAssignedParticipants(
    authToken: string
): Promise<Participant[] | null> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/participants`;

        const response = await fetch(apiUrl, {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            // Handle non-OK responses, e.g., 404 for not found
            return null;
        }

        const res = await response.json();

        // Map the participant data
        const participants: Participant[] = res.map((part: any) => ({
            id: part.id,
            firstName: part.firstName,
            lastName: part.lastName,
            region: part.region,
            org: part.organizationId
        }));


        return participants;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error fetching participants:", error);
        return null;
    }
}


// Function to fetch all participants of an employee & managed employess
export async function getAllParticipants(
    authToken: string,
    includeDeactivated: boolean = false
): Promise<AssignedParticipant[] | null> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/participants/all?includeDeactivated=${includeDeactivated}`;

        const response = await fetch(apiUrl, {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            // Handle non-OK responses, e.g., 404 for not found
            return null;
        }

        const res = await response.json();

        // Map the participant data
        const participants: AssignedParticipant[] = res.map((part: any) => ({
            id: part.id,
            intakeType: part.intakeType,
            firstName: part.firstName,
            lastName: part.lastName,
            deactivated: part.deactivated,
            activationTransitionInProgress: part.activationTransitionInProgress,
            linkageDate: part.linkageDate,
            certificationDate: part.certificationDate,
            cpocDate: part.cpocDate,
            cpocEndDate: part.cpocEndDate,
            region: part.regionId,
            waiver: part.waiver,
            assignee: part.assignee,
            org: part.organizationId,
            creationDate: part.creationDate
        }));

        return participants;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error fetching participants:", error);
        return null;
    }
}

// Function to fetch all participants of an employee & managed employess
export async function getParticipantInfo(
    participantId: string,
    authToken: string
): Promise<AssignedParticipant | "unauthorized" | null> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/participants/${participantId}`;

        const response = await fetch(apiUrl, {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        });

        if (response.status === 403 || response.status === 401) {
            return "unauthorized"; // Explicitly return "unauthorized" if access is denied
        }

        if (!response.ok) {
            // Handle non-OK responses, e.g., 404 for not found
            return null;
        }

        const part = await response.json();
        console.log('Participant', part);

        return part;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error fetching participants:", error);
        return null;
    }
}

export async function toggleParticipantDeactivated(
    participantId: string,
    authToken: string,
    confirmOpenTaskCompletion: boolean = false,
    confirmedOpenTaskCount?: number,
    expectedCurrentDeactivated?: boolean
): Promise<ApiResponse<boolean>> {
    try {
        const query = new URLSearchParams({
            confirmOpenTaskCompletion: String(confirmOpenTaskCompletion),
        });
        if (confirmedOpenTaskCount !== undefined) {
            query.set("confirmedOpenTaskCount", String(confirmedOpenTaskCount));
        }
        if (expectedCurrentDeactivated !== undefined) {
            query.set("expectedCurrentDeactivated", String(expectedCurrentDeactivated));
        }
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/participants/toggleActivation/${participantId}?${query.toString()}`;

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${authToken}`,
            },

        });

        const res = await response.json();
        return res;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error toggling participant:", error);
        throw error;
    }
}

export interface ParticipantDeactivationImpact {
    participantId: number;
    isDeactivated: boolean;
    activationTransitionInProgress: boolean;
    openTaskCount: number;
}

export async function getParticipantDeactivationImpact(
    participantId: string,
    authToken: string
): Promise<ApiResponse<ParticipantDeactivationImpact>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/participants/${participantId}/deactivation-impact`;
    const response = await fetch(apiUrl, {
        headers: {
            Authorization: `Bearer ${authToken}`,
        },
    });

    const result = await response.json();
    return result;
}

export async function editParticipant(
    participantData: {
        dateOfBirth?: string | null;
        id: number;
        participantId: number;
        firstName: string;
        lastName: string;
        email: string;
        mobileNumber: string;
        waiverId: number;
        expectedIntakeType?: "Initial" | "Transfer" | "";
        intakeType?: "Initial" | "Transfer" | "";
        regionId: number;
        countryId?: number | null;
        participantSsnLast4?: string;
        linkageDate?: Date | string | null;
        cpocDate?: Date | string | null;
        cpocEndDate?: Date | string | null;
        certificationDate?: Date | string | null;
    },
    authToken: string
): Promise<ApiResponse<number>> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/participants/edit-participant`;

        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify(participantData),
        });
        const part = await response.json();

        if (!response.ok) {
            console.error("Error editing participant:", response.status);
            return part;
        }

        return part;
    } catch (error) {
        console.error("Error editing participant:", error);
        throw error;
    }
}

// Function to fetch all participants of an employee & managed employess
export async function getWaivers(
    authToken: string
): Promise<ApiResponse<Waiver[]>> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/participants/waiver-families`;

        const response = await fetch(apiUrl, {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        });
        const res = await response.json();
        return res;
    } catch (error) {
        throw error;
    }
}

export interface CountryCode {
    id: number;
    name: string;
    code: string;
}

export async function getCountryCodes(
    authToken: string
): Promise<CountryCode[]> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/ParticipantMFA/all-country-codes`;

        const response = await fetch(apiUrl, {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        });
        const res: CountryCode[] = await response.json();

        return res;
    } catch (error) {
        throw error;
    }
}
export async function postParticipant(
    newParticipant: NewParticipant,
    authToken: string
): Promise<ApiResponse<RegisteredParticipantDTO>> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/participants`;

        const requestBody = {
            firstName: newParticipant.firstName,
            lastName: newParticipant.lastName,
            participantEmail: newParticipant.participantEmail,
            waiverId: newParticipant.waiverId,
            mobileNumber: newParticipant.mobileNumber,
            countryCode: newParticipant.countryCodeId,
            regionId: newParticipant.region,
            caseworker: newParticipant.caseworker,
            linkageDate: formatParticipantDate(newParticipant.linkageDate) || null,
            certificationDate: formatParticipantDate(newParticipant.certificationDate) || null,
            cpocDate: formatParticipantDate(newParticipant.cpocDate) || null,
            cpocEndDate: formatParticipantDate(newParticipant.cpocEndDate) || null,
            dateOfBirth: newParticipant.dateOfBirth || null,
            intakeType: newParticipant.intakeType,
            initial: newParticipant.intakeType ? undefined : newParticipant.initial,
            participantSsnLast4: newParticipant.participantSsnLast4
        }

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody)
        });

        // if (!response.ok) {
        //     const errorData = await response.json();
        //     return { success: false, error: errorData.message || "Failed to create participant." };
        // }

        const res = await response.json();
        return res
    } catch (error) {
        console.error("Error creating participant:", error);
        throw error;
    }
}

export async function postParticipantBump(
    participantId: string,
    waiverid: string,
    authToken: string
): Promise<number | null> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/participants/bump`;

        const requestBody = {
            participantId: participantId,
            waiverId: waiverid
        }

        const response = await fetch(apiUrl, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            // Handle non-OK responses, e.g., 404 for not found
            return null;
        }

        const res: number = await response.json();
        return res;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error creating participant:", error);
        return null;
    }
}

export async function postNewParticipantLinkageDate(
    id: string,
    linkageDate: Date,
    authToken: string
): Promise<number | null> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/participants/linkageDate`;

        const requestBody = {
            participantId: id,
            linkageDate: linkageDate
        }

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            // Handle non-OK responses, e.g., 404 for not found
            return null;
        }

        const res: number = await response.json();
        return res;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error posting new participant linkageDate:", error);
        return null;
    }
}

export async function postNewParticipantCpocDate(
    id: string,
    date: Date,
    authToken: string
): Promise<number | null> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/participants/cpocDate`;

        const requestBody = {
            participantId: id,
            linkageDate: date
        }

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            // Handle non-OK responses, e.g., 404 for not found
            return null;
        }

        const res: number = await response.json();
        return res;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error posting new participant linkageDate:", error);
        return null;
    }
}

export async function postNewParticipantCpocEndDate(
    id: string,
    date: Date,
    authToken: string
): Promise<number | null> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/participants/cpocEndDate`;

        const requestBody = {
            participantId: id,
            linkageDate: date
        }

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            // Handle non-OK responses, e.g., 404 for not found
            return null;
        }

        const res: number = await response.json();
        return res;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error posting new participant linkageDate:", error);
        return null;
    }
}

export async function postNewParticipantCertificationDate(
    id: string,
    date: Date,
    authToken: string
): Promise<number | null> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/participants/certificationDate`;

        const requestBody = {
            participantId: id,
            linkageDate: date
        }

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            // Handle non-OK responses, e.g., 404 for not found
            return null;
        }

        const res: number = await response.json();
        return res;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error posting new participant linkageDate:", error);
        return null;
    }
}

export async function reassigneParticipant(
    participantId: string,
    employeeId: string,
    authToken: string
): Promise<boolean | null> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/participants/transfer`;

        const requestBody = {
            participantId: participantId,
            newAssigneeId: employeeId
        }

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            let message = "Failed to transfer participant";
            try {
                const errorBody = await response.json();
                message = errorBody?.error || errorBody?.message || message;
            } catch {
                // ignore json parsing errors
            }
            throw new Error(message);
        }

        await response.json();
        return true;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error posting new participant linkageDate:", error);
        throw error;
    }
}

export async function GetFilesByParticipantsAsync(
    token: string,
    participantId: string,
    pageNumber: number = 1,
    pageSize: number = 10,
    year?: number | null,
    month?: number | null,
    searchTerm?: string | null,
    sortBy?: string | null,
    sortDirection?: "asc" | "desc" | null,
    signal?: AbortSignal,
): Promise<ApiResponse<GetDetailedFilesForParticipantResponse>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/participants/GetFilesByParticipants`;
    const payload = {
        participantId: participantId,
        pageNumber: pageNumber,
        pageSize: pageSize,
        year: year,
        month: month,
        searchTerm: searchTerm,
        sortBy: sortBy,
        sortDirection: sortDirection,
    }
    try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            signal,
            body: JSON.stringify(payload)
        });
        return await response.json();
    } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
            throw error;
        }
        console.error('Error fetching participant files:', error);
        throw error;
    }
}

export async function getActiveParticipantsAsync(
    token: string,
    req: GetAllActiveParticipantsRequest
): Promise<ApiResponse<PagedResults<ActiveParticipantDTO>>> {
    const queryString = buildQueryV2(req);
    const apiUrl = `/participants/active-participants?${queryString}`;
    return apiGet<ApiResponse<PagedResults<ActiveParticipantDTO>>>(
        apiUrl, token
    );
}

export const registerExistingParticipant = async (
    authToken: string,
    participantId: number,
    participantEmail: string
): Promise<any> => {
    const requestBody = {
        participantId,
        participantEmail,
        returnSecureToken: true,
    };

    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/participants/register-existing`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify(requestBody),
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to register existing participant");
    }

    return response.json();
};

export async function createCIR(
    cirData: {
        participantId: number;
        incidentType: string;
        incidentDate: string;
        location: string;
        description: string;
        injuryOrHarm: boolean;
        witnesses: string[];
        actionsTaken: string;
        attachments: {
            fileName: string;
            fileUrl: string;
        }[];
    },
    authToken: string
): Promise<any> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/Cir/create`;

        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify(cirData),
        });

        if (!response.ok) {
            console.error("Error creating CIR:", response.status);
            return {
                success: false,
                status: response.status,
                message: "Failed to create CIR",
            };
        }

        const result = await response.json();
        return {
            success: true,
            status: response.status,
            data: result,
        };
    } catch (error) {
        console.error("Error creating CIR:", error);
        return {
            success: false,
            status: 500,
            message: "Something went wrong while creating CIR",
        };
    }
}



export async function approveRejectRequest(
    requestId: number, requestData: {
        action: string;
        reason: string;
        id: number;
        participantId: number;
        firstName: string;
        lastName: string;
        email: string;
        mobileNumber: string;
        countryName: string;
        waiverId: number;
        countryId: number;
        regionId: number;
        participantSsnLast4?: number;
    }, authToken: string): Promise<any> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/admin/request/approve-reject/${requestId}`;

        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify(requestData),
        });

        if (!response.ok) {
            console.error("Error approving/rejecting request:", response.status);
            return false;
        }

        return response;
    } catch (error) {
        console.error("Error approving/rejecting request:", error);
        return false;
    }
}

export async function GetAllParticipantUnderMeAsync(
    token: string,
    userId: string
): Promise<ApiResponse<ParticipantUnderMeDto[]>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/participants/userId/${userId}/GetParticipantUnderMe`;

    try {
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching participants:', error);
        throw error;
    }
}

export async function GetListOfCIR(
    token: string,
    payload: GetListOfCirReq
): Promise<ApiResponse<PagedResults<GetCirListResDto>>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Cir/GetListOfCIR`;

    try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching participants:', error);
        throw error;
    }
}

export async function GetCountOfCIRAsync(
    token: string,
    userId: string,
    signal?: AbortSignal
): Promise<ApiResponse<GetCountOfCIR>> {
    const apiUrl = `/Cir/user/${userId}/GetCountOfCIR`;

    return apiGet<ApiResponse<GetCountOfCIR>>(apiUrl, token, signal);
}

export async function UpdateCIRApprovalStatusAsync(
    token: string,
    approvalRequestId: string,
    status: "APPROVED" | "REJECTED",
    rejectionReason?: string
): Promise<ApiResponse<string>> {

    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/cir/approval/${approvalRequestId}`;

    const body: any = { status };

    if (status === "REJECTED") {
        body.rejectionReason = rejectionReason || "";
    }

    try {
        const response = await fetch(apiUrl, {
            method: 'PATCH',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });

        const data = await response.json();
        return data;

    } catch (error) {
        console.error('Error updating CIR approval status:', error);
        throw error;
    }
}

export async function ManualCIRApprovalAsync(
    token: string,
    cirId: string,
    status: number, // 0 for rejection, 1 for approval
    reason: string
): Promise<ApiResponse<string>> {
    try {
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Cir/manual-approval?id=${cirId}`;

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ status, reason }),
        });

        const data = await response.json();
        return data;

    } catch (error) {
        console.error('Error processing manual CIR approval:', error);
        throw error;
    }
}


export async function GetParticipantByWaiverFamily(
    authToken: string,
    waiverType: string
): Promise<ApiResponse<UserWaiverDTO[]>> {
    const apiUrl: string = `/participants/${waiverType}/GetUserByWaiverFamily`;

    var res = apiGet<ApiResponse<UserWaiverDTO[]>>(
        apiUrl, authToken
    )

    return res;
}

export async function GetAssignableParticipantsAsync(
    token: string,
    assigneeId: string
): Promise<ApiResponse<ParticipantUnderMeDto[]>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/participants/assignable-participants/${assigneeId}`;

    try {
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching assignable participants:', error);
        throw error;
    }
}

export interface ParticipantTagDto {
    id: number;
    name: string;
    color?: string | null;
    isCustom?: boolean;
}

export interface ParticipantTagsResponseDto {
    participantId: string | number;
    tags: ParticipantTagDto[];
}

export interface ParticipantTagOptionDto {
    id: number;
    name: string;
    custom: boolean;
}

export async function getParticipantTagOptionsAsync(
    token: string,
    organizationId: number,
    search?: string
): Promise<ApiResponse<ParticipantTagOptionDto[]>> {
    const qp = new URLSearchParams({ organizationId: String(organizationId) });
    if (search && search.trim()) {
        qp.set("search", search.trim());
    }

    return apiGet<ApiResponse<ParticipantTagOptionDto[]>>(
        `/ParticipantTags/options?${qp.toString()}`,
        token
    );
}

export async function updateParticipantDescriptionAsync(
    token: string,
    participantId: string,
    description: string
): Promise<ApiResponse<string>> {
    return apiPatch<ApiResponse<string>, { description: string }>(
        `/participants/${participantId}/description`,
        { description },
        token
    );
}

export async function addStandardTagToParticipantAsync(
    token: string,
    participantId: string,
    tagId: number
): Promise<ApiResponse<unknown>> {
    return apiPost<ApiResponse<unknown>, { participantId: number; tagId: number }>(
        `/ParticipantTags/assign`,
        { participantId: Number(participantId), tagId },
        token
    );
}

export async function addCustomTagToParticipantAsync(
    token: string,
    participantId: string,
    name: string
): Promise<ApiResponse<unknown>> {
    return apiPost<ApiResponse<unknown>, { participantId: number; tagName: string }>(
        `/ParticipantTags/write-in`,
        { participantId: Number(participantId), tagName: name },
        token
    );
}

export async function removeTagFromParticipantAsync(
    token: string,
    participantId: string,
    tagId: number
): Promise<ApiResponse<boolean>> {
    return apiPost<ApiResponse<boolean>, { participantId: number; tagId: number }>(
        `/ParticipantTags/remove`,
        { participantId: Number(participantId), tagId },
        token
    );
}

export interface ParticipantTagAssignmentResponseDto {
    participantId: number;
    tagId: number;
    tagName: string;
    custom: boolean;
    assignedAt: string;
}

export async function getParticipantTagsAsync(
    token: string,
    participantId: string
): Promise<ApiResponse<ParticipantTagAssignmentResponseDto[]>> {
    return apiGet<ApiResponse<ParticipantTagAssignmentResponseDto[]>>(
        `/ParticipantTags/participant/${participantId}`,
        token
    );
}

export interface FailedRecord {
    rowNumber: number;
    errorMessage: string;
    recordData: Record<string, any>;
}

export interface BulkUploadResponse {
    isPreview?: boolean;
    validCount?: number;
    initialCount?: number;
    transferCount?: number;
    successCount: number;
    failedCount: number;
    errors: string[];
    failedRecords: FailedRecord[];
}

export async function uploadParticipantsCsv(
    csvFile: File,
    authToken: string,
    preview: boolean = false
): Promise<ApiResponse<BulkUploadResponse>> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/participants/bulk-upload?preview=${preview}`;

        const formData = new FormData();
        formData.append('csvFile', csvFile);

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
            body: formData
        });

        const res = await response.json().catch(() => null);
        if (!res) throw new Error("The upload response could not be read. Check the participant list before retrying an import.");
        if (!response.ok && !res.message) throw new Error("Upload failed. Check the participant list before retrying.");
        return res;
    } catch (error) {
        console.error("Error uploading participants CSV:", error);
        throw error;
    }
}
